import { SiteLayout, PageHero, LightSection, CtaStrip } from "@/components/site/SiteLayout";

const PILLARS = [
  ["Consultant-validated, always", "No raw AI output ever reaches a client. Every extracted fact, every score and every recommendation is reviewed and approved by senior HEOR and market access consultants before it counts."],
  ["Radical score transparency", "Any number we present can be decomposed live: what it measures, how it is weighted, which evidence and market intelligence drove it. If you ask \"why?\", there is always an answer on the table."],
  ["Traceable to source", "Findings link back to the documents and requirements they came from — down to the quoted passage. Your team can audit anything we claim."],
  ["Confidential by design", "Private, access-controlled workspaces per client. Your data is never pooled, never shared across engagements, and never used to train external models."],
  ["Continuously current", "Payer requirements, HTA precedents and competitor outcomes are maintained country-by-country and updated as the landscape moves — your readiness picture doesn't age between meetings."],
  ["Built on consulting heritage", "MAI Matrix™ was built by MarksMan Healthcare — an HEOR, RWE and market access consultancy — to make our own engagements sharper. It encodes how experienced consultants actually work."],
];

const QA = [
  ["Is this a software product we license?", "No. MAI Matrix™ powers MarksMan Healthcare engagements. You get its outputs — and the consultants who stand behind them — not a login and a manual."],
  ["How is AI used, exactly?", "For scale: reading documents, mapping requirements across markets, keeping intelligence current. Judgment — interpretation, validation, recommendation — stays with senior consultants, with a human sign-off gate before anything enters your analysis."],
  ["How do you handle our confidential documents?", "Under NDA, in a private workspace with access limited to your engagement team. Materials are used solely for your engagement and returned or deleted at your instruction."],
  ["What if we disagree with a score?", "Good — that conversation is the product. Every score decomposes into its inputs, so disagreements resolve into evidence, not opinion."],
];

export default function Trust() {
  return (
    <SiteLayout>
      <PageHero overline="Trust & Validation" title="Confidence you can interrogate."
        lede="Market access decisions carry too much weight for black boxes. Here is how every finding earns its place." />
      <LightSection>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {PILLARS.map(([title, text]) => (
            <div key={title} className="bg-white border border-slate-200 p-6">
              <p className="font-heading font-bold text-slate-900 text-sm">{title}</p>
              <p className="text-sm text-slate-500 leading-relaxed mt-2">{text}</p>
            </div>
          ))}
        </div>
      </LightSection>
      <LightSection alt overline="Straight answers" title="The questions procurement and legal will ask anyway.">
        <div className="space-y-px bg-slate-200 border border-slate-200">
          {QA.map(([q, a]) => (
            <div key={q} className="bg-white p-6 grid grid-cols-1 md:grid-cols-12 gap-3">
              <p className="font-heading font-bold text-slate-900 text-sm md:col-span-4">{q}</p>
              <p className="text-sm text-slate-500 leading-relaxed md:col-span-8">{a}</p>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mt-8">
          {["AI-assisted", "Consultant-validated", "Evidence-traceable", "Confidential engagement", "Board-ready", "Continuously updated"].map((t) => (
            <span key={t} className="text-[10px] uppercase tracking-widest font-bold px-2.5 py-1.5 border border-slate-300 text-slate-500">{t}</span>
          ))}
        </div>
      </LightSection>
      <CtaStrip text="Bring your hardest question to the first call. That's what it's for." />
    </SiteLayout>
  );
}
